import React, { useState, useEffect, useRef } from 'react';
import './DatePicker.css';

const WEEKDAYS = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'];
const MONTH_NAMES = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

function toStr(d) {
  if (!d || !d.getTime) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseStr(s) {
  if (!s || typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Własny wybór daty – ciemny motyw dopasowany do strony */
function DatePicker({ value, onChange, min, max, placeholder = 'Wybierz datę', label, id, className = '', align = 'right', inline = false }) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const v = parseStr(value);
    return v || new Date();
  });
  const ref = useRef(null);

  useEffect(() => {
    const v = parseStr(value);
    if (v) setViewDate(v);
  }, [value]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const valDate = parseStr(value);
  const minDate = parseStr(min);
  const maxDate = parseStr(max);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const pick = (d) => {
    const date = new Date(year, month, d);
    const str = toStr(date);
    if (minDate && date < minDate) return;
    if (maxDate && date > maxDate) return;
    onChange(str);
    setOpen(false);
  };

  const prevMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const lastDayPrevMonth = new Date(year, month, 0);
  const firstDayNextMonth = new Date(year, month + 1, 1);
  const canPrev = !minDate || lastDayPrevMonth >= minDate;
  const canNext = !maxDate || firstDayNextMonth <= maxDate;

  const displayLabel = value ? toStr(valDate).split('-').reverse().join('.') : placeholder;

  return (
    <div className={`kpi-date-picker-wrap ${inline ? 'kpi-date-picker-inline' : ''} ${className} ${open ? 'open' : ''}`} ref={ref}>
      {label && <span className="kpi-date-picker-label">{label}</span>}
      <button
        type="button"
        id={id}
        className="kpi-date-picker-trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span>{displayLabel}</span>
        <span className="kpi-date-picker-chevron">▼</span>
      </button>
      {open && (
        <div className={`kpi-date-picker-dropdown kpi-date-picker-dropdown-${align}`}>
          <div className="kpi-date-picker-header">
            <button type="button" className="kpi-date-picker-nav" onClick={prevMonth} disabled={!canPrev} aria-label="Poprzedni miesiąc">‹</button>
            <span className="kpi-date-picker-month-year">{MONTH_NAMES[month]} {year}</span>
            <button type="button" className="kpi-date-picker-nav" onClick={nextMonth} disabled={!canNext} aria-label="Następny miesiąc">›</button>
          </div>
          <div className="kpi-date-picker-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w} className="kpi-date-picker-weekday">{w}</span>
            ))}
          </div>
          <div className="kpi-date-picker-grid">
            {days.map((d, i) => {
              if (d == null) return <span key={`e-${i}`} className="kpi-date-picker-cell empty" />;
              const date = new Date(year, month, d);
              const str = toStr(date);
              const isSelected = valDate && toStr(valDate) === str;
              const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate);
              return (
                <button
                  key={d}
                  type="button"
                  className={`kpi-date-picker-cell ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && pick(d)}
                  disabled={isDisabled}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="kpi-date-picker-actions">
            <button type="button" className="kpi-date-picker-action" onClick={() => { onChange(''); setOpen(false); }}>Wyczyść</button>
            <button type="button" className="kpi-date-picker-action" onClick={() => { onChange(toStr(new Date())); setOpen(false); }}>Dziś</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
